import React from 'react';
import { locationIcon } from './LocationChoiceModal';
import CartRow from '../../Elements/CartRow';
import CartFooter from '../../Elements/CartFooter';
import EmptyState from '../../Elements/EmptyState';
import { calcularTotalesCarrito } from '../../../lib/cartTotals';

/**
 * Carrito del POS (Dispensario / Barra).
 *
 * Desde 08-2026 el pintado sale de los componentes del sistema —`CartRow` y
 * `CartFooter`, contrapartidas de `Club_cart row` (3204:25971) y
 * `Club_CartFooter` (3155:11495)— y aquí solo queda la aritmética del carrito
 * y lo que el frame no cubre.
 *
 * Lo que el frame NO cubre y sigue vivo aquí, colgado del hueco `extra` de la
 * fila: la ubicación de la que sale cada línea (regla de oro de stock), el peso
 * real de la báscula y el descuento manual por línea.
 *
 * Los productos por gramo van como `pesoFijo`, no como `peso`: el frame los
 * dibuja con gramos y euros tecleables, pero aquí el peso se fija al añadir y
 * no se ajusta después, para no descuadrar stock y merma. Para cambiarlo se
 * quita la línea y se vuelve a añadir.
 */
const Cart = ({
  cartItems = [],
  locById = {},
  onRemoveFromCart,
  onRemoveProduct,
  onClearCart,
  clientCredit,
  discountRate = 0,
  onCheckout,
  onAddQuantity,
  onReduceQuantity,
  onSetQuantity,
  stockWarning,
  /** Solo el dispensario puede quedar en negativo; en barra el backend rechaza la venta. */
  stockWarningAllowsNegative = false,
  onContinueShopping,
  onAddCredit,
  error,
  otherArea = null,
  onChangeLineDiscount,
}) => {
  // Una sola fórmula para el flujo entero (`lib/cartTotals`): la barra inferior,
  // este panel y el cobro. Vivía copiada en los tres sitios y la de la barra se
  // había quedado sin descuentos.
  //
  // Todo lo de aquí sale del helper, incluido el `total`: rehacer el último paso
  // a mano (`subtotal - descuento`) era una quinta copia de la fórmula, y en
  // cuanto el helper redondea a céntimos —lo hace— este panel se habría quedado
  // con el número sin redondear mientras la barra y el cobro usaban el otro.
  // El subtotal tampoco es ya una prop del llamante: esa era la puerta por la
  // que el panel mezclaba un subtotal sin sanear con un descuento saneado y
  // pintaba «Total €NaN» mientras la barra daba un número correcto.
  const {
    subtotal,
    descuentoSocio: memberDiscountAmount,
    descuentoManual: manualDiscountAmount,
    total,
  } = calcularTotalesCarrito(cartItems, discountRate);
  const clientCreditValue = Number(clientCredit) || 0;
  // Se permite cobrar aunque el saldo quede negativo (el socio queda debiendo).
  // Solo bloquea el carrito vacío.
  const canCheckout = cartItems.length > 0;

  // Items del otro área (solo lectura): el carrito muestra "todo" (barra + dispensario).
  const otherItems = otherArea?.items || [];
  const otherLabel = otherArea?.cartType === 'dispensario' ? 'Dispensario' : 'Barra / Productos';
  // SUBTOTAL, no total, y a propósito: este bloque es de solo lectura y sus
  // `CartRow` imprimen el importe BRUTO de cada línea. Descontar solo la cabecera
  // dejaba un «€5.00» encima de una fila de «€10.00» sin nada que los reconcilie
  // —el bloque no tiene desglose—, que es peor que ir en bruto de arriba abajo.
  // Para ponerlo neto de verdad habría que descontar también las filas y añadir
  // su desglose; hasta entonces, la cabecera suma exactamente lo que se ve.
  // Del helper igual, para que un precio que llegue mal no pinte «€NaN».
  const otherSubtotal = calcularTotalesCarrito(otherItems).subtotal;

  /** Cambiar cantidad desde el contador de la fila. */
  const cambiarCantidad = (item, n) => {
    if (onSetQuantity) return onSetQuantity(item, n);
    const actual = Number(item.quantity) || 0;
    if (n > actual) onAddQuantity?.(item, n - actual);
    else if (n < actual) onReduceQuantity?.(item, actual - n);
  };

  /** Lo que el frame no dibuja pero el POS necesita bajo cada línea. */
  const detalleLinea = (item) => {
    const ubicacion = item.location_id && locById[String(item.location_id)];
    // La cantidad grande de la fila ya es la de báscula y la nota «pedido · merma»
    // la pinta CartRow: aquí NO se repite (en staging salía dos veces, 05-09-2026).
    if (!ubicacion && !onChangeLineDiscount) return null;

    return (
      <div className="mt-0.5 flex flex-col gap-0.5">
        {ubicacion && (
          <div className="flex min-w-0 items-center gap-1 text-[11px] text-[var(--muted-foreground)]">
            {/* size 12 para la fila de 11px: el Lucide no escala con el texto como el emoji */}
            <span aria-hidden className="flex-shrink-0">{locationIcon(ubicacion.kind, { size: 12 })}</span>
            <span className="truncate">desde {ubicacion.name}</span>
          </div>
        )}
        {onChangeLineDiscount && (
          <label className="flex items-center gap-1 text-[11px] text-[var(--muted-foreground)]">
            <span>Dto.</span>
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              value={item.manual_discount ?? ''}
              onChange={(e) => onChangeLineDiscount(item, e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder="0"
              className="w-12 rounded-sm border border-[var(--border)] px-1 py-0.5 text-right tabular-nums focus:border-[var(--primary)] focus:outline-none"
            />
            <span>%</span>
          </label>
        )}
      </div>
    );
  };

  const vacio = cartItems.length === 0 && otherItems.length === 0;

  // Columna que rellena el panel: scrollea SOLO la lista (líneas, aviso de stock, otro
  // área) y el pie con las acciones queda debajo, siempre a la vista, hermano de la
  // lista y no dentro de ella. Como pie sticky dentro del mismo scroll solo se pegaba
  // mientras su bloque contenedor estuviera en pantalla: con el carrito largo quedaba
  // por debajo y no se veía «Validar compra» (Kong House, 08-2026). Es el mismo
  // patrón que CarritoPanel. Candados: CartTabletReachability.wave5 (estructura) y
  // tests/e2e/cart-tablet-landscape.spec.js (geometría real a 1280×800).
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        data-testid="cart-scroll"
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-3 sm:px-4 sm:py-4"
      >
        {vacio ? (
          <EmptyState title="Tu carrito está vacío." />
        ) : (
          <>
            {cartItems.length > 0 && (
              <>
                <ul className="divide-y divide-[var(--border)]">
                  {cartItems.map((item, idx) => (
                    <li key={item.id ?? idx}>
                      <CartRow
                        item={item}
                        medida={item.unit_type === 'weight' ? 'pesoFijo' : 'unidad'}
                        onQuantityChange={(n) => cambiarCantidad(item, n)}
                        onRemove={onRemoveProduct || onRemoveFromCart}
                        extra={detalleLinea(item)}
                      />
                    </li>
                  ))}
                </ul>

              </>
            )}

            {/* Otro área (solo lectura): el carrito muestra TODO lo del socio.
                La barra y el dispensario se cobran por separado, así que aquí solo
                se informa; para editar/validar, cambia de área. */}
            {otherItems.length > 0 && (
              <div className={cartItems.length > 0 ? 'mt-5 border-t border-[var(--border)] pt-4' : ''}>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-[var(--muted-foreground)]">
                    También en {otherLabel}
                  </span>
                  <span className="text-xs font-medium text-[var(--muted-foreground)]">
                    €{otherSubtotal.toFixed(2)}
                  </span>
                </div>
                <ul className="divide-y divide-[var(--border)] opacity-90">
                  {otherItems.map((item, idx) => (
                    <li key={item.id ?? idx}>
                      <CartRow
                        item={item}
                        medida={item.unit_type === 'weight' ? 'pesoFijo' : 'unidad'}
                        readOnly
                      />
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-[11px] text-[var(--muted-foreground)]">
                  Cambia a «{otherLabel}» para editar o validar estos productos.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Lo de abajo NO scrollea con las líneas: aviso de stock, error y pie van juntos y
          siempre a la vista. Si en una pantalla baja (móvil apaisado, vista partida) no
          cabe, scrollea ESTE bloque, con tope: el panel es overflow-hidden y, sin esto,
          los botones quedarían recortados sin forma de llegar a ellos. */}
      {cartItems.length > 0 && (
        <div
          data-testid="cart-pie"
          className="max-h-[70%] shrink-0 overflow-y-auto overscroll-contain border-t border-[var(--border)]"
        >
          {/* Aviso de stock: bloque propio y visible. Como línea fina al pie pasaba
              desapercibido — el staff pedía 15 de algo con 3 y "no pasaba nada". Va aquí,
              fuera del scroll de las líneas: con el carrito largo, detrás de la última
              línea nadie lo veía (review del 19-09-2026).

              Sigue SIN bloquear el cobro (el stock va desfasado a menudo), pero la
              segunda línea depende del modo: solo el dispensario admite negativo. En
              barra, `make_purchase` devuelve 400 "Stock insuficiente" salvo
              `is_dispensario`, así que prometer "puedes cobrar igualmente" era mentira
              — el staff se enteraba al cobrar, que es lo que este aviso venía a evitar. */}
          {stockWarning && (
            <div
              role="alert"
              className="mx-3 mt-3 rounded-lg border border-[var(--toast-warning-border)] bg-[var(--toast-warning-bg)] px-3 py-2.5 sm:mx-4"
            >
              <div className="text-xs font-semibold text-[var(--toast-warning-text)]">
                {stockWarning}
              </div>
              <div className="mt-0.5 text-xs text-[var(--warning)]">
                {stockWarningAllowsNegative
                  ? 'Puedes cobrar igualmente, pero el stock quedará en negativo.'
                  : 'Ajusta la cantidad o repón el stock: al cobrar, la venta se rechazará.'}
              </div>
            </div>
          )}

          {error && <p className="mx-3 mt-2 text-xs text-[var(--destructive)] sm:mx-4">{error}</p>}

          <CartFooter
            subtotal={subtotal}
            count={cartItems.length}
            balance={clientCredit != null ? clientCreditValue : null}
            discounts={[
              {
                label: `Descuento (${(discountRate * 100).toFixed(0)}%)`,
                amount: memberDiscountAmount,
              },
              { label: 'Descuento manual', amount: manualDiscountAmount },
            ]}
            total={total}
            disabled={!canCheckout}
            onCheckout={onCheckout}
            onClear={onClearCart}
            onContinue={onContinueShopping}
            onAddCredit={onAddCredit}
          />
        </div>
      )}
    </div>
  );
};

export default Cart;
